<template>
    <RPDropdownButton
        dropdown-position="left"
        :class="statusBuilder('Topbar', status)"
        modal-header=""
    >
        <button class="Topbar__button">
            <div
                v-if="activeSection"
                class="Topbar__header"
            >
                <StreamlineIcon
                    v-if="hasIcon(activeSection.header)"
                    class="Topbar__header-icon"
                    :icon="activeSection.header.icon"
                    :stroke="grey200"
                    :size="16"
                />
                {{ activeSection.header.label }}
            </div>
            <RPNavTab
                v-if="!!selectedTab"
                class="Topbar__tab"
                clas
                :label="selectedTab.label"
                :is-active="true"
                :display="true"
            />
            <RPSubTab
                v-if="showSubTab"
                tag="div"
                class="Topbar__subtab"
                :label="dynamicTab === '' ? selectedSubTab.label : dynamicTab"
                :is-active="true"
            />
        </button>

        <template v-slot:dropdown="{close}">
            <RPSidebar
                class="Topbar__dropdown"
                :tabs="tabs"
                :sections="sections"
                :active-tab="activeTab"
                :dynamic-tab="dynamicTab"
                @navigate="to => navigate(to, close)"
            />
        </template>
    </RPDropdownButton>
</template>
<script>
import RPNavTab from 'components/Basic/Buttons/RPNavTab.vue';
import RPSubTab from 'components/Basic/Buttons/RPSubTab.vue';
import RPSidebar from './RPSidebar.vue';
import stat from 'status/index';
import statusBuilder from 'status/statusBuilder';
import RPDropdownButton from '@/components/Basic/Buttons/DropdownButton/RPDropdownButton.vue';
import StreamlineIcon from "@reviewpush/streamline-icons-vue";
import {grey200} from 'style/_colors.scss';
import {has} from 'lodash';

export default {
    name: 'RPTopbar',
    components: {
        RPNavTab,
        RPSubTab,
        RPSidebar,
        RPDropdownButton,
        StreamlineIcon
    },
    props: {
        /**
         * A list of tabs to display. See description for more detail
         */
        tabs: {
            type: Array,
            default: () => ([])
        },
        sections: {
            type: Array,
            default: () => ([])
        },
        /**
         * The route name for the currently active tab
         */
        activeTab: {
            type: String,
            required: true,
        },
        /**
         * Displays text underneath the currently active tab. Useful to hint when
         * the user has a current subpage/section open
         */
        dynamicTab: {
            type: String,
            default: '',
        },
        status: {
            type: String,
            default: stat.STANDBY,
        },
    },
    data() {
        return {
            grey200
        };
    },
    computed: {
        activeSection(){
            return this.sections.find(section => {
                return section.tabs.find(tab => tab.to.name === this.activeTab);
            });
        },
        fullTabs(){
            return [
                ...this.tabs,
                ...this.sections.map(s => s.tabs).flat()
            ];
        },
        fullSubTabs(){
            return this.fullTabs
                .filter(tab => has(tab, 'subTabs'))
                .map(tab => tab.subTabs).flat();
        },
        selectedTab(){
            return this.fullTabs.find(tab => {
                return ((tab.to.name === this.activeTab)||(has(tab, 'subTabs') && tab.subTabs.find(t => t.to.name === this.activeTab)));
            });
        },
        selectedSubTab(){
            if(has(this.selectedTab, 'subTabs')){
                return this.selectedTab.subTabs.find(tab => tab.to.name === this.activeTab);
            }
            return undefined;
        },
        showSubTab(){
            return !!this.selectedSubTab || this.dynamicTab !== '';
        }
    },
    methods: {
        statusBuilder,
        hasIcon(header){
            return has(header, 'icon');
        },
        navigate(to, close){
            this.$emit('navigate', to);
            close();
        }
    }
};
</script>
<style lang="scss">
    .Topbar {
        width: 100%;
        @include loading(3.25rem, 100%);

        &__button {
            width: 100%;
            padding: 0.5rem;
            background: $white;
            border-radius: 4px;
            display: flex;
            align-items: center;
        }
        &__header {
            display: flex;
            align-items: center;
            &-icon { margin-right: 0.25rem; }
            margin-right: 0.5rem;
        }
        &__subtab {
            margin-left: 0.5rem;
        }
        &__dropdown {
            margin-top: 0.25rem;
        }
    }
</style>

<docs>
```vue
import {FolderText} from '@reviewpush/streamline-regular/lib/files-folders/Folders';
<RPTopbar
    :active-tab="myValString"
    :tabs="[{'to':{'name':'UserNotificationsSettings'},'label':'Notifications'},{'to':{'name':'UserReports'},'label':'Reports'},{'to':{'name':'MyProfile'},'label':'My Profile'}]"
    @navigate="nav => myValString = nav.name"
>
</RPTopbar>
<br>
<br>
<RPTopbar
    :active-tab="myValString"
    :sections="[
        {
            'header':{
                label: 'Collective Reports',
                icon: FolderText,
            },
            'tabs':[
                {'to':{'name':'OverviewReport'},'label':'Overview'},
                {'to':{'name':'LocationsReport'},'label':'Locations'},
                {'to':{'name':'SentimentReport'},'label':'Sentiment'},
                {'to':{'name':'CompetitorsReport'},'label':'Competitors'}
            ]
        },
        {
            'header':{
                label: 'Focused Reports',
                icon: FolderText,
            },
            'tabs':[
                {'to':{'name':'ReviewsReport'},'label':'Reviews'},
                {'to':{'name':'RatingsReport'},'label':'Ratings'},
                {'to':{'name':'KeywordsReport'},'label':'Keywords'},
                {'to':{'name':'LandingPagesReport'},'label':'Landing Pages'},
                {'to':{'name':'CampaignsReport'},'label':'Campaigns'},
                {'to':{'name':'ReviewResponsesReport'},'label':'Review Responses'}
            ]
        }
    ]"
    @navigate="nav => myValString = nav.name"
>
</RPTopbar>
<br>
<br>
<RPTopbar
    active-tab="SampleDynamic"
    dynamic-tab="Sample Dynamic"
    :sections="[
        {
            'header':{ label: 'General' },
            'tabs':[
                {'to':{'name':'GlobalNotifications'},'label':'Notifications'},
                {'to':{'name':'Billing'},'label':'Billing'}
            ]
        },
        {
            'header':{ label: 'Company' },
            'tabs':[
                {
                    'to':{'name':'BrandsRegionsTable'},
                    'label':'Brands/Regions',
                    'subTabs':[
                        {'to':{'name':'SampleDynamic'}}
                    ],
                },
                {'to':{'name':'UsersTable'},'label':'Users'},
                {'to':{'name':'UserRolesTable'},'label':'User Roles'},
                {'to':{'name':'ThirdPartyLoginsTable'},'label':'3rd Party Accounts'}
            ]
        },
        {
            'header':{ label: 'Product Tools' },
            'tabs':[
                {'to':{'name':'CompanyReports'},'label':'Reports'},
                {'to':{'name':'LandingPagesTable'},'label':'Landing Pages'},
                {'to':{'name':'SuggestedResponseTable'},'label':'Suggested Responses'},
                {'to':{'name':'EmbeddedReviewsTable'},'label':'Embedded Reviews'},
                {'to':{'name':'ReviewPushAPI'},'label':'ReviewPush API'}
            ]
        }
    ]"
    @navigate="nav => myValString = nav.name"
>
</RPTopbar>
<br>
<br>
<RPTopbar
    :active-tab="myValString"
    :tabs="[
        {
            'key': 'overview',
            'label': 'Overview',
            'to': { 'name': 'LocationOverview'  }
        },
        {
            'key': 'reviews',
            'label': 'Reviews',
            'to': {'name': 'LocationReviews' }
        },
        {
            'key': 'profiles',
            'label': 'Profiles',
            'to': {'name': 'LocationProfiles' }
        },
        {
            'key': 'campaigns',
            'label': 'Campaigns',
            'to': { 'name': 'LocationCampaigns' }
        },
        {
            'key': 'competitors',
            'label': 'Competitors',
            'to': {'name': 'LocationCompetitorsOverview'}
        },
        {
            'key': 'settings',
            'label': 'Settings',
            'to': { 'name': 'LocationInformation' },
            'subTabs': [
                {
                    'label': 'Information',
                    'key': 'information',
                    'to': {'name': 'LocationInformation'}
                },
                {
                    'label': 'Notifications',
                    'key': 'notifications',
                    'to': { 'name': 'LocationNotifications' }
                },
                {
                    'label': 'Competitors',
                    'key': 'competitors',
                    'to': {'name': 'LocationCompetitors' }
                },
                {
                    'label': 'Branding',
                    'key': 'branding',
                    'to': { 'name': 'LocationBranding'  }
                },
                {
                    'label': 'Landing Pages',
                    'key': 'landingpages',
                    'to': {  'name': 'LocationLandingPagesTable'  }
                },
                {
                    'label': 'Embedded Feeds',
                    'key': 'embedded',
                    'to': {'name': 'LocationEmbeddedReviewsTable'  }
                }
            ]
        }
    ]"
    @navigate="nav => myValString = nav.name"
>
</RPTopbar>
```
</docs>